import React, { useState, useEffect } from "react";

import {
    Box,
    Button,
    Paper,
    Divider,
    TextField,
    Grid,
    Switch,
    FormControlLabel,
    MenuItem,
    FormHelperText,
    Card,
    styled,
    InputAdornment,
} from '@mui/material';

import { useFormik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from "react-router-dom"
import { CommonIcons, icons } from "../common/CommonIcons";
import CommonBreadcumbs from '../common/CommonBreadcumbs';
import CommonSectionHeader from "../common/CommonSectionHeader";
import CommonRestricted from '../common/CommonRestricted';
import { useAppContextFeatures } from "../../context/AppContext";

import { useAuthAccess } from "../../hooks/useAuthAccess";
import { useSystemRoutes, routeTypes } from '../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../hooks/usePermissions';
import { useTranslations } from '../../hooks/useTranslations';

import { useRates, rateTypes, targetTypes as rateTargetTypes } from '../../hooks/api/useRates';
import { useSystemPrompt } from "../../hooks/useSystemPrompt";
import { initialValues, validationSchema } from "../../schema/tax/taxRateSchema";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function NewTaxRate({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@tax"] })

    const { promptMessage } = useSystemPrompt();

    const { isLoading, create } = useRates({ rateType: rateTypes.taxRate, targetType: rateTargetTypes.taxRate });
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.rate.id, scopeTypes.rate.taxRate.id)

    const navigate = useNavigate();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const onSubmit = async (currentValues) => {

        try {

            let data = {
                rate: {
                    rate_name: currentValues?.rateName,
                    description: currentValues?.description,
                    status: currentValues?.status ? 1 : 0,
                },
                props: {
                    tax_percentage: parseFloat(currentValues?.taxPercentage).toFixed(2)
                }
            }

            const response = await create({ data });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("settings/taxRates"))

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }

    }
    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    const RenderForm = () => {

        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={0.5}>
                        <Grid item xs={12} sm={12} md={3} textAlign={"left"}>

                            <CommonSectionHeader
                                title={t('rateInfo')}
                                subTitle={t('detailsOfTaxRateCharges')}
                            />

                        </Grid>
                        <Grid item xs={12} sm={12} md={9}>

                            <Grid container spacing={1}>
                                <Grid item xs={12} sm={12} md={6}>
                                    <FormControlLabel
                                        sx={{
                                            marginLeft: '1px'
                                        }}
                                        control={
                                            <Switch
                                                sx={{
                                                    mt: 1
                                                }}
                                                name="status"
                                                checked={Boolean(values.status)}
                                                onChange={(event, checked) => {
                                                    setFieldValue("status", checked);
                                                }}
                                            />
                                        }
                                        label={
                                            Boolean(values.status) ? t("enabled") : t("disabled")
                                        }
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <></>
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        autoFocus
                                        fullWidth
                                        name={"rateName"}
                                        label={t("rateName")}
                                        type="text"
                                        variant="outlined"
                                        value={values.rateName}
                                        onChange={handleChange}
                                        error={touched.rateName && Boolean(errors.rateName)}
                                        helperText={touched.rateName && errors.rateName}
                                    />
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"taxPercentage"}
                                        label={t("taxPercentage")}
                                        type="text"
                                        variant="outlined"
                                        value={values.taxPercentage}
                                        onChange={handleChange}
                                        error={touched.taxPercentage && Boolean(errors.taxPercentage)}
                                        helperText={touched.taxPercentage && errors.taxPercentage}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <CommonIcons name={icons.percentage} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"description"}
                                        label={t("description")}
                                        type="text"
                                        multiline
                                        rows={2}
                                        variant="outlined"
                                        value={values.description}
                                        onChange={handleChange}
                                        error={touched.description && Boolean(errors.description)}
                                        helperText={touched.description && errors.description}
                                    />
                                </Grid>

                            </Grid>
                        </Grid>
                    </Grid>
                </StyledCard>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>

        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.newTaxRate })
            }

            <CommonBreadcumbs
                pageTitle={t("newTaxRate")}
                breadcrumbs={[
                    {
                        displayText: t("taxRates"),
                        props: {
                            // underline: "hover",
                            href: getRoute("settings/taxRates")
                        }
                    },
                    {
                        displayText: t("newTaxRate"),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageNew.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}